import React from 'react'
import PropTypes from 'prop-types'
import { Box, Icon, Loading, Text } from '@maui/elements'
import isEmpty from 'lodash/isEmpty'

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'Complete':
      return <Icon name='check-circle' color='colorFeedbackSuccess' mr={5}/>
    case 'Cancelled':
      return <Icon name='cross-circle' color='colorFeedbackSuccess' mr={5}/>
    case 'In Progress':
      return <Box mr={5}><Loading /></Box>
    case 'Solution Suggested':
      return <Icon name='check-circle' color='colorFeedbackWarning' mr={5}/>
    case 'Awaiting Customer':
    case 'Pending Customer':
      return <Icon name='ellipsis-circle' color='colorFeedbackWarning' mr={5}/>
    default:
      return null
  }
}

StatusIcon.propTypes = {
  status: PropTypes.string
}

const formatShortDate = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' }
  return new Date(dateString).toLocaleDateString(undefined, options)
}

const listGridStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  listStyleType: 'none',
  margin: '0',
  padding: '0',
}

const ListGrid = ({ children }) => (
  <Box as="ul" style={listGridStyles}>
    {children}
  </Box>
)

ListGrid.propTypes = {
  children: PropTypes.node,
}

const ListGridItem = ({
  children,
  cellWidth = ['100%', '50%', '33%', '25%'],
  ...props
}) => (
  <Box
    as="li"
    display="flex"
    alignItems="flex-start"
    marginBottom="8px"
    paddingRight="16px"
    width={cellWidth}
  >
    <Box display="flex" gap="8px" width="100%" justifyContent="space-between" {...props}>
      {children}
    </Box>
  </Box>
)

ListGridItem.propTypes = {
  children: PropTypes.node,
  cellWidth: PropTypes.arrayOf(PropTypes.string),
}

export const ServiceDetailsListGrid = ({
  case_number: id = '',
  services_name: serviceName,
  status,
  contact_name: contactName = '',
  requested_date: requestedDate,
  completed_date: completedDate,
  unit_cost: unitCost,
  subject = '',
  service_requested: serviceRequested,
}) => {
  return (
    <ListGrid>
      <ListGridItem cellWidth={['50%', '50%']}>
        <Text
          variant="body2"
          fontWeight="300"
          whiteSpace="nowrap"
        >
          Title
        </Text>
        <Text
          variant="body2"
          fontWeight="bold"
          textAlign="right"
          maxWidth="125px"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          { subject?.length > 25 ? subject.substring(0, 25) + '...' : subject }
        </Text>
      </ListGridItem>
      <ListGridItem cellWidth={['50%', '50%']}>
        <Text
          variant="body2"
          fontWeight="300"
          whiteSpace="nowrap"
        >
          Completed
        </Text>
        <Text
          variant="body2"
          fontWeight="bold"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {formatShortDate(completedDate) === 'Invalid Date'
            ? '---'
            : formatShortDate(completedDate.replace(/-/g, '/'))}
        </Text>
      </ListGridItem>
      <ListGridItem cellWidth={['50%', '50%']}>
        <Text
          variant="body2"
          fontWeight="300"
          whiteSpace="nowrap"
        >
          Service Name
        </Text>
        <Text
          variant="body2"
          fontWeight="bold"
          textAlign="right"
          maxWidth="125px"
        >
          {serviceName}
        </Text>
      </ListGridItem>
      <ListGridItem cellWidth={['50%', '50%']}>
        <Text
          variant="body2"
          fontWeight="300"
          whiteSpace="nowrap"
        >
          Contact Name
        </Text>
        <Text
          variant="body2"
          fontWeight="bold"
          textAlign="right"
          maxWidth="125px"
        >
          {contactName?.length > 25 ? contactName.substring(0, 25) + '...' : contactName}
        </Text>
      </ListGridItem>
      <ListGridItem cellWidth={['50%', '50%']}>
        <Text
          variant="body2"
          fontWeight="300"
          whiteSpace="nowrap"
        >
          Case ID
        </Text>
        <Text
          variant="body2"
          fontWeight="bold"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {id}
        </Text>
      </ListGridItem>
      <ListGridItem cellWidth={['50%', '50%']}>
        <Text
          variant="body2"
          fontWeight="300"
          whiteSpace="nowrap"
        >
          Status
        </Text>
        <Box display='flex'>
          <StatusIcon status={status} />
          <Text
            variant="body2"
            fontWeight="bold"
            textAlign="right"
            maxWidth="125px"
          >
            {status}
          </Text>

        </Box>
      </ListGridItem>
      <ListGridItem cellWidth={['50%', '50%']}>
        <Text
          variant="body2"
          fontWeight="300"
          whiteSpace="nowrap"
        >
          Requested
        </Text>
        <Text
          variant="body2"
          fontWeight="bold"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {formatShortDate(requestedDate) === 'Invalid Date'
            ? '---'
            : formatShortDate(requestedDate.replace(/-/g, '/'))}
        </Text>
      </ListGridItem>
      <ListGridItem cellWidth={['50%', '50%']}>
        <Text
          variant="body2"
          fontWeight="300"
          whiteSpace="nowrap"
        >
          Service Requested
        </Text>
        <Text
          variant="body2"
          fontWeight="bold"
          maxWidth="135px"
          textAlign="right"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {serviceRequested}
        </Text>
      </ListGridItem>
      <ListGridItem cellWidth={['50%', '50%']}>
        <Text
          variant="body2"
          fontWeight="300"
          whiteSpace="nowrap"
        >
          Unit Cost
        </Text>
        <Text
          variant="body2"
          fontWeight="bold"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {isEmpty(unitCost) || unitCost === '-'
            ? '---'
            : `${Number(unitCost.split(' ')[0]).toFixed()} units`
          }
        </Text>
      </ListGridItem>
    </ListGrid>
  )
}

ServiceDetailsListGrid.propTypes = {
  services_name: PropTypes.string,
  status: PropTypes.string,
  contact_name: PropTypes.string,
  requested_date: PropTypes.string,
  completed_date: PropTypes.string,
  unit_cost: PropTypes.string,
  subject: PropTypes.string,
  case_number: PropTypes.string,
  service_requested: PropTypes.string,
}

ServiceDetailsListGrid.defaultProps = {
  services_name: '---',
  status: '---',
  contact_name: '---',
  requested_date: '',
  completed_date: '',
  unit_cost: '---',
  subject: '---',
  case_number: '---',
}
